import { VehicleCameraPairingResponse } from '$Generated/api';
import { React, moment } from 'Imports';
import { useEffect, useState } from 'react';
import { RequestVideoHeader } from '../RequestVideoHeader';
import { IDropdownItem } from '$Components/FilterComponents/VPDropdown';
import { isEncompass } from '$Utilities/integrationPlatform';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { faInfoCircle as faInfoCircleRegular } from '@fortawesome/pro-regular-svg-icons';
import { FleetSettingsService } from '$State/FleetSettingsFreezerService';
import FootageSegmentPreview from './FootageSegmentPreview';
import { CameraSegmentBase, CameraSegmentFullData } from '../TimeAndDurationStep/FootageSegments';
import { DeviceAssociationService } from '$State/DeviceAssociationFreezerService';

const styles = require('./SegmentPreviewStep.scss') as {
    content: string;
    geotab: string;
    encompass: string;
    titleDescriptionWrapper: string;
    title: string;
    description: string;
    previewWrapperGeotab: string;
    previewWrapperEncompass: string;
    timezoneBanner: string;
    timezoneBannerIcon: string;
    timezoneBannerBody: string;
    timezoneBannerHeading: string;
    timezoneBannerText: string;
    timezoneBannerConversion: string;
    timezoneBannerArrow: string;
};

interface ISegmentPreviewStepProps {
    pairing: VehicleCameraPairingResponse;
    selectedDate: Date;
    requestStartDate?: Date;
    requestEndDate?: Date;
    selectedSegment: CameraSegmentFullData;
    recallStartDate?: Date;
    selectedDuration: number | undefined;
    durationOptions: IDropdownItem[];
    onRecallStartDateChange: (date: Date | undefined) => void;
    onDurationChange: (duration: number | undefined) => void;
    onSegmentChange: (segment: CameraSegmentBase) => void;
    onValidationErrorChange?: (hasError: boolean) => void;
}

export const SegmentPreviewStep = (props: ISegmentPreviewStepProps) => {
    const { pairing, selectedDate, selectedSegment, requestStartDate, requestEndDate } = props;

    const [fleetTimezone, setFleetTimezone] = useState<string | null>(null);
    // The conversion banner is only meaningful next to a usable preview, so it waits for the footage to
    // finish loading rather than sitting beside the loading bar or an error message.
    const [isPreviewReady, setIsPreviewReady] = useState(false);
    // The header's online/offline tag is sourced from a pairing snapshot fetched before polling began, so
    // it needs to be corrected once the preview detects the camera went offline while loading.
    const [isCameraDisconnected, setIsCameraDisconnected] = useState(false);

    useEffect(() => {
        const fetchTimezone = async () => {
            const cached = FleetSettingsService.getState().getVideoOverlayTimezone;
            if (!cached.hasFetched) {
                await FleetSettingsService.getVideoOverlayTimezone();
            }
            const timezone = FleetSettingsService.getState().getVideoOverlayTimezone.data?.timezone ?? null;
            setFleetTimezone(timezone);
        };
        fetchTimezone();
    }, []);

    // Selecting/changing a segment invalidates whatever recall time was previously chosen -- playing the new
    // preview will re-establish it (see VideoPreview's playback-progress reporting). Clearing it here, rather
    // than preemptively defaulting to the new segment's start, keeps Submit disabled and the header's "Recall
    // Time" blank while that new preview is still loading, instead of showing/allowing a selection the user
    // hasn't actually seen yet.
    useEffect(() => {
        props.onRecallStartDateChange(undefined);
    }, [selectedSegment]);

    const userTimezone = moment.tz.guess();
    const footageTimezone = fleetTimezone ?? userTimezone;
    // Only claim a mismatch when the footage's zone is actually known -- otherwise there is nothing to
    // convert between and the banner would be comparing the user's zone against itself.
    const timezonesDiffer =
        fleetTimezone !== null &&
        moment(selectedSegment.startTime).tz(userTimezone).utcOffset() !== moment(selectedSegment.startTime).tz(fleetTimezone).utcOffset();

    const formatTimeIn = (date: Date, timezone: string) => moment(date).tz(timezone).format('hh:mm:ss A');
    const timezoneAbbr = (timezone: string) => moment(selectedSegment.startTime).tz(timezone).format('z');

    // Re-fetches both cached pairing lists once the preview detects the camera went offline: Video Recall's
    // drawer reads from deviceAssociationResults, Device Association's reads from availableDevicesResults.
    // Both are existing methods on the same service -- no new state, no new plumbing.
    const handleCameraDisconnectedChange = (isDisconnected: boolean) => {
        setIsCameraDisconnected(isDisconnected);
        if (isDisconnected) {
            DeviceAssociationService.getDeviceAssociations();
            DeviceAssociationService.getAvailableDevices();
        }
    };

    return (
        <>
            <RequestVideoHeader
                pairing={pairing}
                selectedDate={selectedDate}
                requestStartDate={requestStartDate}
                requestEndDate={requestEndDate}
                isCameraDisconnected={isCameraDisconnected}
            />
            <div className={`${styles.content} ${isEncompass ? styles.encompass : styles.geotab}`}>
                <div className={isEncompass ? styles.previewWrapperEncompass : styles.previewWrapperGeotab}>
                    <div className={styles.titleDescriptionWrapper}>
                        <div className={styles.title}>Preview Footage & Select Recall Time</div>
                        <div className={styles.description}>
                            Pause the video at the moment you want to recall. You can also enter a time manually and change the duration of
                            the recall as needed.
                        </div>
                    </div>
                    <FootageSegmentPreview
                        cameraSegment={selectedSegment}
                        fleetTimezone={fleetTimezone}
                        recallStartDate={props.recallStartDate}
                        selectedDuration={props.selectedDuration}
                        durationOptions={props.durationOptions}
                        onSegmentChange={props.onSegmentChange}
                        onRecallStartDateChange={props.onRecallStartDateChange}
                        onDurationChange={props.onDurationChange}
                        onValidationErrorChange={props.onValidationErrorChange}
                        onPreviewReadyChange={setIsPreviewReady}
                        onCameraDisconnectedChange={handleCameraDisconnectedChange}
                    />
                </div>

                {isPreviewReady && timezonesDiffer && (
                    <div className={styles.timezoneBanner}>
                        <FontAwesomeIcon icon={isEncompass ? faInfoCircle : faInfoCircleRegular} className={styles.timezoneBannerIcon} />
                        <div className={styles.timezoneBannerBody}>
                            <div className={styles.timezoneBannerHeading}>Time Zone Conversion</div>
                            <div className={styles.timezoneBannerText}>
                                The video footage time zone and your time zone do not match. Here is a conversion in case you need it.
                            </div>
                            {requestStartDate && requestEndDate && (
                                <div className={styles.timezoneBannerConversion}>
                                    {`${formatTimeIn(requestStartDate, footageTimezone)} - ${formatTimeIn(requestEndDate, footageTimezone)} `}
                                    <b>{timezoneAbbr(footageTimezone)}</b>
                                    <FontAwesomeIcon icon={faArrowRight} className={styles.timezoneBannerArrow} />
                                    {`${formatTimeIn(requestStartDate, userTimezone)} - ${formatTimeIn(requestEndDate, userTimezone)} `}
                                    <b>{timezoneAbbr(userTimezone)}</b>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default SegmentPreviewStep;
